import { CheckIcon, PlusCircleIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  useCommandContext,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

interface MultiSelectorOption {
  id: string
  label: string
}

interface MultiSelectorProps {
  title: string
  options: MultiSelectorOption[]
  selected: string[]
  setFilterValue: (value: string[]) => void
}

function MultiSelectorOptions({
  options,
  selected,
  setFilterValue,
}: Omit<MultiSelectorProps, 'title'>) {
  const { query } = useCommandContext()
  const selectedValues = new Set(selected)
  const normalizedQuery = query.trim().toLowerCase()
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(normalizedQuery)
  )

  const toggle = (id: string) => {
    const next = new Set(selectedValues)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }

    setFilterValue(Array.from(next))
  }

  if (!filteredOptions.length) {
    return <CommandEmpty>Sin resultados.</CommandEmpty>
  }

  return (
    <>
      <CommandGroup>
        {filteredOptions.map((option) => {
          const isSelected = selectedValues.has(option.id)

          return (
            <CommandItem key={option.id} onClick={() => toggle(option.id)}>
              <div
                className={cn(
                  'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                  isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'opacity-50 [&_svg]:invisible'
                )}
              >
                <CheckIcon className="h-4 w-4" />
              </div>
              <span>{option.label}</span>
            </CommandItem>
          )
        })}
      </CommandGroup>

      {selectedValues.size > 0 && (
        <>
          <CommandSeparator />
          <CommandGroup>
            <CommandItem
              onClick={() => setFilterValue([])}
              className="justify-center text-center"
            >
              Resetear filtro
            </CommandItem>
          </CommandGroup>
        </>
      )}
    </>
  )
}

export function MultiSelector({
  title,
  options,
  selected,
  setFilterValue,
}: MultiSelectorProps) {
  const selectedValues = new Set(selected)
  const selectedLabels = options.filter((option) => selectedValues.has(option.id))

  const truncateText = (text: string, maxLength = 10) =>
    text.length > maxLength ? `${text.substring(0, maxLength - 3)}...` : text

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-10 justify-start border-border bg-background px-3 text-gray-700 hover:bg-accent"
        >
          <PlusCircleIcon className="h-4 w-4" />
          {title}
          {selectedValues.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-1 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex lg:gap-0.5">
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {selectedValues.size}
                  </Badge>
                ) : (
                  selectedLabels.map((option) => (
                    <Badge
                      variant="secondary"
                      key={option.id}
                      className="rounded-sm px-1 font-normal"
                    >
                      {truncateText(option.label, 14)}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} className="h-9" />
          <CommandList>
            <MultiSelectorOptions
              options={options}
              selected={selected}
              setFilterValue={setFilterValue}
            />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
